import {ChangeDetectorRef, EventEmitter} from '@angular/core';
import {INewsPost, INewsTagProperty} from '@api/models/news/news';
import {formatDate} from '@angular/common';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {IPicture} from '@api/models/image/picture';
import {ImageService} from '@utils/services/image/image.service';
import {ImageSubType} from '@api/models/image/image-type';
import {UrlService} from '@utils/services/url/url.service';
import {IChip} from '@lib-modules/chips/models/chip';

export abstract class NewsComponentBase {

  public abstract isDetailView: boolean;
  public abstract openGallery: EventEmitter<number>;
  public abstract openDetail: EventEmitter<any>;
  public abstract newsPost: INewsPost;

  public timeString = '';
  public date: string;
  public time: string;
  public dateTime: string;
  public text = '';
  public images: IPicture[] = [];
  public hasImages = false;
  public hasVideo = false;
  public hasNativeVideo = false;
  public tags: IChip[] = [];
  public loading = true;
  public creatorLogoUrl: string;
  public readonly maxPreviewLength = 69; // Nice

  constructor( protected changeDetectorRef: ChangeDetectorRef ) {}

  public abstract onImageClick(index?: number): void;

  public init(): void {
    if (this.newsPost.creator?.pictures?.length > 0) {
      this.creatorLogoUrl = ImageService.findPictureUrlBySubType(
        this.newsPost.creator.pictures, ImageSubType.LOGO);
    }
    if (this.newsPost.preview_text && !this.isDetailView) {
      this.text = this.newsPost.preview_text;
    }
    if (this.newsPost.text && this.isDetailView) {
      this.text = this.newsPost.text;
    }
    this.hasNativeVideo = this.newsPost.videos_native?.length > 0;
    this.hasVideo = this.newsPost.media?.videoURL?.length > 0 || this.hasNativeVideo;
    if (this.newsPost.pictures) {
      this.images = this.newsPost.pictures;
      this.hasImages = this.newsPost.pictures?.length > 0;
    }
    this.date = formatDate(this.newsPost.timestamp, 'dd.MM.yyyy', 'de-DE');
    this.time = formatDate(this.newsPost.timestamp, 'HH:mm', 'de-DE');
    this.dateTime = `${this.date}, ${this.time} Uhr`;
    this.timeString = this.getTimeString();
    if (this.newsPost.ui_tags) {
      const tagsObject = this.newsPost.ui_tags;
      const tagsList = [];
      if (tagsObject.category_tag) {
        tagsList.push(tagsObject.category_tag);
      }
      Object.keys(this.newsPost.ui_tags).forEach((key) => {
        if (Array.isArray(tagsObject[key])) {
          tagsList.push(...tagsObject[key]);
        }
      });
      this.tags = this.getTagsWithLink(tagsList);
      if (this.tags.length === 0) {
        this.tags.push({
          name: 'Allgemein',
        });
      }
    }
    this.changeDetectorRef.detectChanges();
  }

  private getTagsWithLink(tags: INewsTagProperty[]): IChip[] {
    const newsTags = tags.map((tag) => {
      return {
        name: tag.ui_name,
        link: tag.readable_id ? UrlService.getSubdomainUrl(tag.readable_id) : undefined,
      };
    });
    return newsTags || [];
  }

  public isShared(): boolean {
    if (!this.newsPost.media) {
      return false;
    }
    if (this.newsPost.media.facebookURL || this.newsPost.media.twitterURL) {
      return true;
    }
    return false;
  }

  public isImported(): boolean {
    return this.newsPost.author?.type === 'facebook';
  }

  public getSharedLink(): string {
    if (this.newsPost.media.facebookURL) {
      return this.newsPost.media.facebookURL;
    }
    if (this.newsPost.media.twitterURL) {
      return this.newsPost.media.twitterURL;
    }
    return '';
  }

  private getTimeString(): string {
    const target = new Date(this.newsPost.timestamp);
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(now.getDate() - 1);

    let diff = Date.now() - this.newsPost.timestamp;
    const isFuture = diff < 0;
    if (isFuture) {
      if (target.getDate() === now.getDate()) {
        return `geplant für Heute ${this.time} Uhr`;
      }
      return `geplant für ${this.dateTime}`;
    }

    const hoursDifference = Math.floor( diff / 1000 / 60 / 60);
    diff -= hoursDifference * 1000 * 60 * 60;
    const minutesDifference = Math.floor(diff / 1000 / 60);
    diff -= minutesDifference * 1000 * 60;
    const secondsDifference = Math.floor(diff / 1000);

    if (target < now) {
      if (target < yesterday) {
        return this.dateTime;
      }
      if (target < today) {
        return `Gestern`;
      }
    }
    if (hoursDifference > 0) {
      if (hoursDifference === 1) {
        return `vor einer Stunde `;
      }
      return `vor ${hoursDifference} Stunden`;
    }
    if (minutesDifference > 1) {
      if (minutesDifference === 1) {
        return `vor einer Minute `
      }
      return `vor ${minutesDifference} Minuten`;
    }
    if (secondsDifference < 60) {
      return 'Gerade eben';
    }
  }
}
